import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from '@tyro/i18n';
import { useState } from 'react';
import { useResolvedRelatedDocs } from '../../hooks/use-related-docs';
import type { PageMetadataRow } from '../../utils/page-metadata';
import { PageKindIcon } from '../page-kind-icon';
import { SectionLabel } from './section-label';

const SEE_ALSO_LIMIT = { default: 3, xl: 5 } as const;

interface ReaderSeeAlsoSectionProps {
  metadata: PageMetadataRow[] | undefined;
  onSelectRelatedDoc?: (spaceId: number, pageSlug: string) => void;
}

export function ReaderSeeAlsoSection({
  metadata,
  onSelectRelatedDoc,
}: ReaderSeeAlsoSectionProps) {
  const { t } = useTranslation(['documentation']);
  const relatedDocs = useResolvedRelatedDocs(metadata);
  const isXLViewport = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up('xl'),
  );
  const seeAlsoLimit = isXLViewport
    ? SEE_ALSO_LIMIT.xl
    : SEE_ALSO_LIMIT.default;
  const [showAll, setShowAll] = useState(false);

  if (relatedDocs.length === 0) return null;

  const canCollapse = relatedDocs.length > seeAlsoLimit;
  const visible =
    canCollapse && !showAll ? relatedDocs.slice(0, seeAlsoLimit) : relatedDocs;

  return (
    <Stack sx={{ mt: 4, gap: 1.5 }}>
      <SectionLabel>{t('documentation:reader.seeAlso')}</SectionLabel>
      <Card>
        <Stack divider={<Divider />}>
          {visible.map((entry) => (
            <ListItemButton
              key={`${entry.spaceSlug}/${entry.pageSlug}`}
              onClick={() =>
                onSelectRelatedDoc?.(entry.spaceId, entry.pageSlug)
              }
              disabled={!onSelectRelatedDoc}
              sx={{ px: 2, py: 1.25, gap: 1.5 }}
            >
              <PageKindIcon
                entityType={entry.entityType}
                withTooltip
                sx={{ color: 'text.disabled' }}
              />
              <Typography
                variant="body2"
                noWrap
                sx={{ flex: 1, minWidth: 0, color: 'text.primary' }}
              >
                {entry.title}
              </Typography>
              <Chip
                label={entry.spaceName}
                variant="outlined"
                size="small"
                sx={{
                  flexShrink: 0,
                  height: 24,
                  fontSize: 12,
                  color: 'text.secondary',
                }}
              />
            </ListItemButton>
          ))}
        </Stack>
      </Card>
      {canCollapse && (
        <Box sx={{ pl: 0.5 }}>
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            color="primary"
            onClick={() => setShowAll((value) => !value)}
            sx={{ fontWeight: 600 }}
          >
            {showAll
              ? t('documentation:reader.showLess')
              : t('documentation:reader.showMore', {
                  count: relatedDocs.length - seeAlsoLimit,
                })}
          </Link>
        </Box>
      )}
    </Stack>
  );
}
